import { appInsights } from "./appInsights";

type PendingTiming<TContext extends object> = TContext & {
  startedAt: number;
};

type TimingPropertiesBuilder<TContext extends object> = (
  context: TContext,
  durationMs: number,
  success: boolean,
  errorMessage?: string,
) => Record<string, unknown>;

interface PendingTimingConfig<TContext extends object> {
  metricName: string;
  completedEventName: string;
  failedEventName: string;
  buildProperties: TimingPropertiesBuilder<TContext>;
}

export function getTelemetryErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message?: unknown }).message ?? "Unknown error");
  }

  return "Unknown error";
}

export function createPendingTiming<TContext extends object>({
  metricName,
  completedEventName,
  failedEventName,
  buildProperties,
}: PendingTimingConfig<TContext>) {
  const pendingItems = new Map<string, PendingTiming<TContext>>();

  function trackResult({
    context,
    startedAt,
    success,
    error,
  }: {
    context: TContext;
    startedAt: number;
    success: boolean;
    error?: unknown;
  }) {
    const durationMs = performance.now() - startedAt;
    const errorMessage = success ? undefined : getTelemetryErrorMessage(error);
    const properties = buildProperties(context, durationMs, success, errorMessage);

    appInsights.trackMetric({
      name: metricName,
      average: durationMs,
      sampleCount: 1,
      properties: {
        ...properties,
        success: String(success),
        durationMs: String(durationMs),
      } as never,
    });

    appInsights.trackEvent({
      name: success ? completedEventName : failedEventName,
      properties,
    });
  }

  return {
    trackCompleted(context: TContext, startedAt: number) {
      trackResult({ context, startedAt, success: true });
    },
    trackFailed(context: TContext, startedAt: number, error: unknown) {
      trackResult({ context, startedAt, success: false, error });
    },
    start(id: string, context: TContext) {
      pendingItems.set(id, {
        ...context,
        startedAt: performance.now(),
      });
    },
    complete(id: string): boolean {
      const pending = pendingItems.get(id);
      if (!pending) return false;

      pendingItems.delete(id);
      trackResult({
        context: pending,
        startedAt: pending.startedAt,
        success: true,
      });
      return true;
    },
    fail(id: string, error: unknown): boolean {
      const pending = pendingItems.get(id);
      if (!pending) return false;

      pendingItems.delete(id);
      trackResult({
        context: pending,
        startedAt: pending.startedAt,
        success: false,
        error,
      });
      return true;
    },
    has(id: string): boolean {
      return pendingItems.has(id);
    },
    clear(id: string) {
      pendingItems.delete(id);
    },
  };
}
